import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath, Outlet, useLocation } from 'react-router'
import { Box, Stack } from '@mui/material'

import { getDashboardOptions } from '@/modules/dashboard/utils/dashboardConstants'
import { useNavigate } from '@/modules/urlRouting/hooks'

import { DashboardDownloadProvider } from '../DashboardDownloadProvider'

import { DashboardHeader } from './DashboardHeader'

/**
 * The layout of a dashboard group (Time and Distance, Micromobility Parking). The route renders
 * it around the page of each slide: one header for the group, with the slide controls and the
 * download in it, then the chart card of the current slide.
 */
const DashboardLayout: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const GROUP_TITLES = {
    trip: t('dashboard.timeAndDistance', 'Time and Distance'),
    parking: t('dashboard.micromobilityParking', 'Micromobility Parking'),
  }

  const activeOption = getDashboardOptions().find(
    option => !!matchPath(option.value, location.pathname)
  )
  if (!activeOption) return null

  const slides = getDashboardOptions().filter(option => option.group === activeOption.group)
  const activeIndex = slides.findIndex(option => option.value === activeOption.value)

  return (
    <DashboardDownloadProvider>
      <Stack height="100%">
        <DashboardHeader
          title={GROUP_TITLES[activeOption.group as keyof typeof GROUP_TITLES]}
          slides={slides}
          activeIndex={activeIndex}
          onSelect={index => navigate(slides[index].value)}
        />
        {/*
          The `main` landmark puts the chart inside a landmark (axe `region`). The header stays
          outside it, so it keeps its `banner` role.
        */}
        <Box
          component="main"
          flex={1}
          minHeight={0}
          overflow="auto"
          // The same space around the content as the curb pages (for example Occupancy).
          py={2}
          px={3}
          display="flex"
          flexDirection="column"
        >
          <Outlet />
        </Box>
      </Stack>
    </DashboardDownloadProvider>
  )
}

export default DashboardLayout
